/* eslint-disable react/no-danger */
import React from 'react';

import { Text } from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { useTranslate } from '@lumapps/translations';
import { currentLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { User } from '@lumapps/user/types';
import { sanitizeHTML } from '@lumapps/utils/string/sanitizeHtml';

import { getNotificationMeta } from '../notification-meta';
import { Notification } from '../types';
import { isModernNotification } from '../utils/isModernNotification';

const allowedTags = ['strong', 'b', 'em'];
const useDescription = (notification: Notification, senderDetails?: User | null) => {
    const translation = useTranslate();
    const currentLanguage = useSelector(currentLanguageSelector);

    return React.useMemo(() => {
        if (isModernNotification(notification)) {
            return (
                <Text
                    as="span"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: sanitizeHTML(notification.text, { allowedTags }) }}
                />
            );
        }

        const { type, description } = notification;
        const meta = getNotificationMeta(type);

        if (meta) {
            // get the title
            const title = meta.getTitle
                ? meta.getTitle({
                      notification,
                      count: notification.actionCount,
                      senderDetails,
                      translation,
                      currentLanguage,
                  })
                : null;

            const metaDescription =
                meta.getDescription({
                    notification,
                    count: notification.actionCount,
                    senderDetails,
                    translation,
                    currentLanguage,
                }) || '';

            return (
                <>
                    {title && (
                        <>
                            <Text
                                as="span"
                                dangerouslySetInnerHTML={{ __html: sanitizeHTML(title, { allowedTags }) }}
                            />{' '}
                            <br />
                        </>
                    )}
                    <Text
                        as="span"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                            __html: sanitizeHTML(metaDescription, { allowedTags }),
                        }}
                    />
                </>
            );
        }

        return (
            <Text
                as="span"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(translation.translateObject(description || {}) || '', { allowedTags }),
                }}
            />
        );
    }, [currentLanguage, notification, senderDetails, translation]);
};

export { useDescription };
